import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from './store.js';

/**
 * Route wrapper for anything that needs a signed-in user.
 *
 * This is a *convenience*, not a security boundary. The server rejects
 * unauthenticated API calls with a 401 regardless of what the client renders;
 * hiding a route in the browser protects nobody. It exists so people see a
 * login prompt instead of an empty page full of failed requests.
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const status = useAuth((s) => s.status);
  const refresh = useAuth((s) => s.refresh);
  const location = useLocation();

  useEffect(() => {
    if (status === 'unknown') void refresh();
  }, [status, refresh]);

  // Render nothing while the session is still unknown, rather than flashing
  // the login page at someone who is already signed in.
  if (status === 'unknown') {
    return <p className="text-neutral-600 dark:text-neutral-400">Checking your session…</p>;
  }

  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
